import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Power, Package2 } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useBetonProduit, useDeleteProduit } from '@/hooks/useBetonCatalog';
import { ProduitFormDialog } from '@/components/operations/beton/ProduitFormDialog';
import { BomEditor } from '@/components/operations/beton/BomEditor';
import { FAMILLE_LABELS, MACHINE_LABELS } from '@/types';
import { formatFCFA, formatNumber } from '@/utils/currency';
import { demander } from '@/components/ui/Fenetres';

export const ProduitDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const { data: produit, isLoading, error } = useBetonProduit(id);
  const remove = useDeleteProduit();

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Chargement…</div>;
  }
  if (error || !produit) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Produit introuvable
        </div>
        <Link to="/operations/catalogue" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
          ← Retour au catalogue
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!(await demander.confirmer({ titre: `Désactiver le produit "${produit.code}" ?`, message: 'Le BOM sera préservé.', confirmer: 'Désactiver', danger: true }))) return;
    await remove.mutateAsync(produit.id);
    navigate('/operations/catalogue');
  };

  const { coutRevient } = produit;

  return (
    <>
      <Header
        title={produit.designation}
        subtitle={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs">{produit.code}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-gray-600">
              {FAMILLE_LABELS[produit.famille]}
            </span>
            {!produit.actif && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-700">
                Inactif
              </span>
            )}
          </span>
        }
        actions={
          <>
            <Link to="/operations/catalogue">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="size-4" /> Retour
              </Button>
            </Link>
            {produit.actif && (
              <Button variant="secondary" size="sm" onClick={handleDelete} loading={remove.isPending}>
                <Power className="size-4" /> Désactiver
              </Button>
            )}
            <Button size="sm" onClick={() => setEditOpen(true)}>
              <Edit className="size-4" /> Modifier
            </Button>
          </>
        }
      />

      <div className="space-y-6 p-6">
        {/* Coût de revient */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card accent>
            <CardContent className="!pt-5">
              <p className="text-xs uppercase tracking-wider text-gray-500">Prix de vente HT</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{formatFCFA(produit.prixVenteCents)}</p>
              <p className="text-xs text-gray-400">par {produit.uniteVente}</p>
            </CardContent>
          </Card>
          {(() => {
            // Ticket SIMGROUP-2026-002 M-05 : coût 0 == BOM non renseigné.
            // Une marge à 100% calculée sur un coût nul est un faux signal
            // de rentabilité. On l'affiche comme "non calculable" en orange.
            const coutValue = Number(coutRevient.coutRevientCents ?? 0);
            const bomRenseigne = coutValue > 0;
            return (
              <>
                <Card>
                  <CardContent className="!pt-5">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Coût de revient</p>
                    {bomRenseigne ? (
                      <>
                        <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
                          {formatFCFA(coutRevient.coutRevientCents)}
                        </p>
                        <p className="text-xs text-gray-400">basé sur le BOM courant</p>
                      </>
                    ) : (
                      <>
                        <p className="mt-1 text-lg font-semibold text-amber-700">Non renseigné</p>
                        <p className="text-xs text-amber-600">Renseignez la nomenclature (BOM) pour calculer le coût</p>
                      </>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="!pt-5">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Marge</p>
                    {bomRenseigne ? (
                      <>
                        <p
                          className={`mt-1 text-2xl font-bold tabular-nums ${
                            coutRevient.margeCents >= 0 ? 'text-emerald-600' : 'text-red-600'
                          }`}
                        >
                          {formatFCFA(coutRevient.margeCents)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {coutRevient.margePct.toFixed(1)}% du prix de vente
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="mt-1 text-lg font-semibold text-amber-700">Non calculable</p>
                        <p className="text-xs text-amber-600">Coût de revient inconnu — marge non vérifiable</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </div>

        {/* BOM */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Nomenclature (BOM) — matières premières</CardTitle>
              <Link to="/operations/matieres">
                <Button variant="ghost" size="sm">
                  <Package2 className="size-4" /> Gérer les MP
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <BomEditor produitId={produit.id} initialItems={produit.bomItems} />
          </CardContent>
        </Card>

        {/* Caractéristiques techniques */}
        <Card>
          <CardHeader>
            <CardTitle>Caractéristiques techniques</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3 lg:grid-cols-4">
              <Spec label="Groupe" value={produit.groupe ? `${produit.groupe.libelle}${produit.groupe.actif ? '' : ' (désactivé)'}` : 'Non classé'} />
              <Spec label="Précision (classe, type)" value={produit.sousFamille} />
              <Spec label="Longueur (m)" value={produit.longueurM} />
              <Spec label="Largeur (mm)" value={produit.largeurMm != null ? String(produit.largeurMm) : null} />
              <Spec label="Hauteur (mm)" value={produit.hauteurMm != null ? String(produit.hauteurMm) : null} />
              <Spec label="Poids (kg)" value={produit.poidsKg} />
              <Spec label="fc28 (MPa)" value={produit.resistanceFc28} />
              <Spec label="Norme" value={produit.norme} />
              <Spec label="Classe" value={produit.classeProduit} />
              <Spec label="Effort nominal" value={produit.effortNominalDaN != null ? `${produit.effortNominalDaN} daN` : null} />
              <Spec label="Charge rupture" value={produit.chargeRuptureDaN != null ? `${produit.chargeRuptureDaN} daN` : null} />
              <Spec label="Machine" value={produit.machineFabrication ? MACHINE_LABELS[produit.machineFabrication] : null} />
              <Spec label="Temps cycle" value={produit.tempsCycleMin ? `${produit.tempsCycleMin} min` : null} />
            </dl>
          </CardContent>
        </Card>

        {/* Stock */}
        <Card>
          <CardHeader>
            <CardTitle>Stock</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Disponible au parc</p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
                  {formatNumber(produit.disponibleParc ?? 0)} {produit.uniteVente}
                </p>
                {(produit.reserveParc ?? 0) > 0 && (
                  <p className="mt-0.5 text-[11px] text-gray-500">
                    dont {formatNumber(produit.reserveParc)} réservé{produit.reserveParc > 1 ? 's' : ''} à une commande
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Seuil d'alerte</p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
                  {formatNumber(parseFloat(produit.seuilAlerte))} {produit.uniteVente}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Sorti du parc</p>
                <p className="mt-1 text-lg font-medium tabular-nums text-gray-700">
                  {formatNumber(produit.sortiParc ?? 0)} {produit.uniteVente}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-400">cumul des expéditions</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Cumul de production</p>
                <p className="mt-1 text-lg font-medium tabular-nums text-gray-700">
                  {formatNumber(parseFloat(produit.stockActuel))} {produit.uniteVente}
                </p>
                {/* Ce compteur historique n'est pas une disponibilité : il est
                    incrémenté à la clôture d'un OF et ne redescend jamais. Il
                    reste affiché, nommé pour ce qu'il est, plutôt que retiré en
                    silence — un chiffre qu'on a longtemps lu comme un stock ne
                    doit pas disparaître sans explication. */}
                <p className="mt-0.5 text-[11px] text-gray-400">
                  compteur historique, ne tient pas compte des sorties
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ProduitFormDialog open={editOpen} onClose={() => setEditOpen(false)} produit={produit} />
    </>
  );
};

const Spec = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-gray-400">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900">{value || <span className="text-gray-300">—</span>}</dd>
  </div>
);
